<template>
  <div class="landing-page">
    <!-- Hero with clear hierarchy and visual interest -->
    <section class="hero" aria-labelledby="hero-title">
      <div class="hero-background" aria-hidden="true" />
      <div class="hero-content">
        <h1 id="hero-title" class="hero-title">
          Find high quality <span class="highlight-word">continuing education</span> courses for dietitians
        </h1>
        <p class="hero-subtitle">
          The best place to discover, compare, and choose CE courses.
        </p>
      </div>
    </section>

    <!-- Primary and secondary CTAs as proper links/buttons -->
    <div class="cta-container">
      <router-link to="/search" class="cta-primary">
        <span class="cta-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        </span>
        <span>Find your next CE course</span>
      </router-link>
      <router-link v-if="!isAuthenticated" to="/signup" class="cta-secondary">
        Create account
      </router-link>
      <router-link v-else :to="{ name: 'profile' }" class="cta-secondary">
        My profile
      </router-link>
    </div>

    <!-- Recently Reviewed Courses -->
    <section class="courses-section" aria-labelledby="section-reviewed-title">
      <div class="section-band">
        <div class="section-header">
          <h2 id="section-reviewed-title" class="section-title">Recently Reviewed Courses</h2>
        </div>
        <div v-if="loadingReviewed" class="loading-state">
          <span class="spinner" aria-hidden="true" />
          <p>Loading recently reviewed courses…</p>
        </div>
        <div v-else-if="recentlyReviewedCourses.length === 0" class="empty-state">
          <span class="empty-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
          </span>
          <p>No recently reviewed courses yet.</p>
          <router-link to="/search" class="empty-link">Browse courses</router-link>
        </div>
        <div v-else class="courses-grid">
          <article
            v-for="course in recentlyReviewedCourses"
            :key="course.id"
            class="course-card"
            tabindex="0"
            role="button"
            @click="viewCourse(course.id)"
            @keydown.enter="viewCourse(course.id)"
            @keydown.space.prevent="viewCourse(course.id)"
          >
            <div class="course-card-header">
              <h3 class="course-card-title">{{ course.name }}</h3>
              <div class="course-card-badges">
                <span v-for="comp in (course.competencies || [])" :key="comp.id" class="badge badge-competency">{{ comp.name }}</span>
                <span class="badge badge-credits">{{ course.credits }} CE Credit{{ course.credits !== 1 ? 's' : '' }}</span>
              </div>
            </div>
            <div class="course-card-body">
              <div class="info-row">
                <span class="info-label">Provider:</span>
                <span class="info-value">{{ course.providerName }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Cost:</span>
                <span class="info-value">
                  <span v-if="course.costType === 'free'" class="free-badge">Free</span>
                  <span v-else>${{ formatCost(course.cost) }}</span>
                </span>
              </div>
              <div v-if="course.reviewCount > 0" class="info-row">
                <span class="info-label">Rating:</span>
                <span class="info-value">
                  ⭐ {{ course.averageOverallValue ? course.averageOverallValue.toFixed(1) : 'N/A' }}
                  ({{ course.reviewCount }} review{{ course.reviewCount !== 1 ? 's' : '' }})
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Recently Added Courses -->
    <section class="courses-section" aria-labelledby="section-added-title">
      <div class="section-band">
        <div class="section-header">
          <h2 id="section-added-title" class="section-title">Recently Added Courses</h2>
        </div>
        <div v-if="loadingAdded" class="loading-state">
          <span class="spinner" aria-hidden="true" />
          <p>Loading recently added courses…</p>
        </div>
        <div v-else-if="recentlyAddedCourses.length === 0" class="empty-state">
          <span class="empty-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
          </span>
          <p>No recently added courses yet.</p>
          <router-link to="/search" class="empty-link">Browse courses</router-link>
        </div>
        <div v-else class="courses-grid">
          <article
            v-for="course in recentlyAddedCourses"
            :key="course.id"
            class="course-card"
            tabindex="0"
            role="button"
            @click="viewCourse(course.id)"
            @keydown.enter="viewCourse(course.id)"
            @keydown.space.prevent="viewCourse(course.id)"
          >
            <div class="course-card-header">
              <h3 class="course-card-title">{{ course.name }}</h3>
              <div class="course-card-badges">
                <span v-for="comp in (course.competencies || [])" :key="comp.id" class="badge badge-competency">{{ comp.name }}</span>
                <span class="badge badge-credits">{{ course.credits }} CE Credit{{ course.credits !== 1 ? 's' : '' }}</span>
              </div>
            </div>
            <div class="course-card-body">
              <div class="info-row">
                <span class="info-label">Provider:</span>
                <span class="info-value">{{ course.providerName }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Cost:</span>
                <span class="info-value">
                  <span v-if="course.costType === 'free'" class="free-badge">Free</span>
                  <span v-else>${{ formatCost(course.cost) }}</span>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Format:</span>
                <span class="info-value">{{ formatCourseType(course.courseType) }}</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getRecentlyReviewedCourses, getRecentlyAddedCourses } from '../../services/courseService.js'
import { formatCourseType as formatCourseTypeModel } from '../../models/Course.js'
import { isAuthenticated as checkAuth } from '../../services/authService.js'

export default {
  name: 'LandingPage',
  computed: {
    isAuthenticated() {
      return checkAuth()
    }
  },
  data() {
    return {
      recentlyReviewedCourses: [],
      recentlyAddedCourses: [],
      loadingReviewed: false,
      loadingAdded: false
    }
  },
  async mounted() {
    await Promise.all([
      this.loadRecentlyReviewed(),
      this.loadRecentlyAdded()
    ])
  },
  methods: {
    handleSearch() {
      this.$router.push({ name: 'search' })
    },
    async loadRecentlyReviewed() {
      this.loadingReviewed = true
      try {
        this.recentlyReviewedCourses = await getRecentlyReviewedCourses()
      } catch (error) {
        console.error('Error loading recently reviewed courses:', error)
        this.recentlyReviewedCourses = []
      } finally {
        this.loadingReviewed = false
      }
    },
    async loadRecentlyAdded() {
      this.loadingAdded = true
      try {
        this.recentlyAddedCourses = await getRecentlyAddedCourses()
      } catch (error) {
        console.error('Error loading recently added courses:', error)
        this.recentlyAddedCourses = []
      } finally {
        this.loadingAdded = false
      }
    },
    viewCourse(courseId) {
      this.$router.push({ name: 'view-course', params: { id: courseId } })
    },
    formatCost(cost) {
      if (!cost) return '0.00'
      return parseFloat(cost).toFixed(2)
    },
    formatCourseType(type) {
      return formatCourseTypeModel(type)
    }
  }
}
</script>

<style scoped>
/* Spacing scale: 8px base → 8, 16, 24, 32, 40, 48, 64 */
.landing-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px;
}

/* Hero: hierarchy + subtle background */
.hero {
  position: relative;
  text-align: center;
  margin-bottom: 48px;
  padding: 32px 24px;
  border-radius: 16px;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    var(--color-bg-main) 0%,
    var(--color-bg-dark) 50%,
    var(--color-primary-light) 120%
  );
  opacity: 0.15;
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--color-text-primary);
  margin: 0 0 16px 0;
}

.hero-subtitle {
  font-size: 1.125rem;
  color: var(--color-text-secondary);
  margin: 0;
  max-width: 540px;
  margin-left: auto;
  margin-right: auto;
}

/* CTAs: primary as link-styled button, secondary as text link */
.cta-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 64px;
}

.cta-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 32px;
  background: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: 1px solid var(--color-primary-dark);
  border-radius: 10px;
  font-size: 1.0625rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal), border-color var(--transition-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
}

.cta-primary:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.06);
}

.cta-primary:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.cta-primary:focus-visible {
  outline: 2px solid var(--color-primary-dark);
  outline-offset: 2px;
}

.cta-icon {
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
}

.cta-icon svg {
  width: 100%;
  height: 100%;
}

.cta-secondary {
  display: inline-flex;
  align-items: center;
  padding: 16px 24px;
  color: var(--color-primary-main);
  border: 1px solid var(--color-border-main);
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  background: var(--color-surface-light);
  transition: border-color var(--transition-normal), background-color var(--transition-normal), color var(--transition-normal);
}

.cta-secondary:hover {
  border-color: var(--color-primary-main);
  background: var(--color-bg-dark);
  color: var(--color-primary-dark);
}

.cta-secondary:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

/* Section bands for breathing room */
.courses-section {
  margin-top: 64px;
  width: 100%;
}

.section-band {
  background: var(--color-surface-light);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 32px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.section-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-border-light);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

/* Loading: spinner + text */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-main);
  border-top-color: var(--color-primary-main);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state: icon + message + link */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  color: var(--color-text-secondary);
  font-size: 1rem;
  text-align: center;
}

.empty-icon {
  display: flex;
  width: 48px;
  height: 48px;
  color: var(--color-border-dark);
}

.empty-icon svg {
  width: 100%;
  height: 100%;
}

.empty-link {
  margin-top: 8px;
  color: var(--color-primary-main);
  font-weight: 600;
  text-decoration: none;
}

.empty-link:hover {
  text-decoration: underline;
}

.empty-link:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Course grid */
.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

/* Cards: thinner border, subtle shadow, focus */
.course-card {
  background: var(--color-surface-light);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), border-color var(--transition-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.course-card:hover {
  border-color: var(--color-primary-main);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
}

.course-card:active {
  transform: translateY(0);
}

.course-card:focus-visible {
  outline: 2px solid var(--color-primary-main);
  outline-offset: 2px;
}

.course-card-header {
  margin-bottom: 16px;
}

.course-card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 12px 0;
  line-height: 1.35;
}

.course-card-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
}

.badge-competency {
  background: var(--color-primary-light);
  color: var(--color-text-inverse);
}

.badge-credits {
  background: var(--color-secondary-light);
  color: var(--color-text-primary);
}

.course-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  gap: 8px;
}

.info-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  min-width: 80px;
}

.info-value {
  color: var(--color-text-primary);
}

.free-badge {
  background: var(--color-success-light);
  color: var(--color-success-dark);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
}

@media (max-width: 768px) {
  .landing-page {
    padding: 32px 16px;
  }

  .hero {
    padding: 24px 16px;
    margin-bottom: 32px;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .cta-container {
    flex-direction: column;
    margin-bottom: 48px;
  }

  .cta-primary,
  .cta-secondary {
    width: 100%;
    max-width: 320px;
    justify-content: center;
  }

  .courses-section {
    margin-top: 48px;
  }

  .section-band {
    padding: 24px 16px;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .courses-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .course-card {
    padding: 20px;
  }
}
</style>
